"use client";

import Link from "next/link";
import Image from "next/image";
import React, { useMemo } from "react";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";

/* =========================
    TYPES (Strict Data Safety)
========================= */
interface Button {
  id: string;
  url: string;
  text: string;
  type: string; // "primary" | "secondary"
  newTab: boolean;
}

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      alternativeText: string;
    };
  };
}

interface HeroProps {
  data: {
    id: string;
    title: string;
    description: string;
    picture: Picture;
    buttons: Button[];
    isLive: boolean;
    useH1Tag?: boolean;
  };
}

const Hero_LeftFocus = ({ data }: HeroProps) => {
  if (!data?.isLive) return null;

  const picUrl = getStrapiMedia(data.picture?.data?.attributes?.url);
  const picAlt =
    data?.picture?.data?.attributes?.alternativeText ||
    data?.title ||
    "Hero Image";

  // Split title logic for two-tone styling
  const { mainTitle, highlightTitle } = useMemo(() => {
    const titleWords = data?.title?.split(" ") || [];
    return {
      mainTitle: titleWords.slice(0, -2).join(" "),
      highlightTitle: titleWords.slice(-2).join(" "),
    };
  }, [data?.title]);

  const TitleTag = data?.useH1Tag ? "h1" : "h2";

  return (
    <section className="relative w-full h-[500px] md:h-[650px] lg:h-[80vh] overflow-hidden group bg-neutral-900">
      {/* 1. Background Image with Ken Burns Effect */}
      <div className="absolute inset-0 transition-transform duration-[8000ms] ease-out group-hover:scale-110">
        {picUrl && (
          <Image
            src={picUrl}
            alt={picAlt}
            fill
            className="object-cover"
            priority
            quality={95}
          />
        )}
      </div>

      {/* 2. Professional Gradient Overlay (Left Focus) */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent z-10" />

      {/* 3. Global Clickable Layer */}
      {data?.buttons?.[0]?.url && (
        <Link
          href={data.buttons[0].url}
          className="absolute inset-0 z-20"
          target={data.buttons[0].newTab ? "_blank" : "_self"}
          aria-label="Visit our platform"
        />
      )}

      {/* 4. Content Layer - Left Aligned */}
      <div className="relative h-full max-w-[1440px] mx-auto w-full px-6 md:px-12 lg:px-16 flex items-center justify-start z-30 pointer-events-none">
        <div className="max-w-3xl text-left pointer-events-auto">
          {/* Architectural Typography */}
          <div className="mb-6">
            <TitleTag className="text-5xl md:text-8xl font-black text-white uppercase italic tracking-tighter leading-[0.85] drop-shadow-2xl">
              {mainTitle} <br />
              <span style={{ color: "var(--secondary)" }}>
                {highlightTitle}
              </span>
            </TitleTag>
          </div>

          {/* Description */}
          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-md font-medium leading-relaxed drop-shadow-md">
            {data?.description}
          </p>

          {/* 5. MULTIPLE BUTTONS CONTAINER */}
          <div className="flex flex-wrap justify-start gap-4 md:gap-6">
            {data?.buttons?.map((btn, index) => {
              const isPrimary =
                btn.type === "primary" || (!btn.type && index === 0);
              const isSecondary = btn.type === "secondary";

              return (
                <Link
                  key={btn.id || index}
                  href={btn?.url || "#"}
                  target={btn?.newTab ? "_blank" : "_self"}
                  rel={btn?.newTab ? "noopener noreferrer" : undefined}
                  className={`
    px-8 md:px-12 py-4 font-black uppercase text-[10px] md:text-[11px] tracking-[0.2em] transition-all duration-300 shadow-2xl active:scale-95 text-center border
    ${
      isPrimary
        ? "text-white border-transparent hover:bg-white/50 hover:border-white/80 hover:backdrop-blur-md"
        : "bg-white/50 text-white border-white/30 backdrop-blur-md"
    }
  `}
                  onMouseEnter={(e) => {
                    if (isSecondary) {
                      e.currentTarget.style.backgroundColor =
                        "var(--secondary)";
                      e.currentTarget.style.borderColor = "var(--secondary)";
                    } else if (isPrimary) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isSecondary) {
                      e.currentTarget.style.backgroundColor = "";
                      e.currentTarget.style.borderColor = "";
                    } else if (isPrimary) {
                      e.currentTarget.style.backgroundColor =
                        "var(--secondary)";
                    }
                  }}
                  style={{
                    backgroundColor: isPrimary ? "var(--secondary)" : undefined,
                  }}
                >
                  {btn?.text}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero_LeftFocus;
